/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import './style/bluemateSectionCard.scss';

import classNames from 'classnames';

import React, {ReactNode} from 'react';

import {DotsLoader} from '../loader/dotsLoader';

import BluemateButton from '../button/bluemateButton';

import BluemateEmptyState from '../emptyState/bluemateEmptyState';

import BluemateListGroup from '../listGroup/bluemateListGroup';

interface Props {
    /** Centered pill label above the rows. */
    title: ReactNode;
    /** First load — replaces the rows with a loader. */
    loading?: boolean;
    /** No rows to show. Ignored while loading. */
    empty?: boolean;
    emptyLabel?: ReactNode;
    /** Request failed — takes precedence over the empty state. */
    error?: ReactNode;
    hasMore?: boolean;
    /** A follow-up page is on its way; the button reports it instead of the rows. */
    loadingMore?: boolean;
    loadMoreLabel?: ReactNode;
    onLoadMore?: () => void;
    className?: string;
    children: ReactNode;
}

/**
 * Framed section of a dashboard-style page: a titled group of rows with an
 * optional "load more" footer, plus the loading / empty / error states every
 * such section needs.
 */
export default function BluemateSectionCard(props: Props) {
    const {
        title,
        loading,
        empty,
        emptyLabel,
        error,
        hasMore,
        loadingMore,
        loadMoreLabel,
        onLoadMore,
        className,
        children
    } = props;

    function renderBody() {
        if (loading) return <DotsLoader dotSize='1.25rem' className='section-card-loader' />;
        if (error != null) return <div className='section-card-error'>{error}</div>;
        if (empty) return <BluemateEmptyState className='section-card-empty' title={emptyLabel} />;
        return children;
    }

    function renderFooter() {
        if (loading || error != null || empty || !hasMore || !onLoadMore) return null;
        return (
            <div className='section-card-footer'>
                {/* Same props as the orders list's "load more" so both pages
                    show one button, not two variants of it. */}
                <BluemateButton
                    label={loadMoreLabel}
                    secondary
                    shape='rounded'
                    loading={loadingMore}
                    onClick={onLoadMore}
                />
            </div>
        );
    }

    return (
        <section className={classNames('bluemate-section-card', className)}>
            <BluemateListGroup title={title}>{renderBody()}</BluemateListGroup>
            {renderFooter()}
        </section>
    );
}
